import { useState } from 'react'
import { somarItens, type EtapaDto, type MotivoPerdaDto, type NegociacaoDto } from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useMoverEtapa } from '../../api/hooks-leads'
import { Aviso, Botao, Campo, Modal } from '../../componentes/ui'
import { formatarMoeda, lerMoeda } from '../../formato'

/**
 * Entrar em etapa de ganho ou de perda exige registro (ADR 0002): sem ele o
 * histórico some quando o card for reciclado numa negociação nova.
 *
 * No ganho, o vendedor digita o que foi vendido, item por item (não há
 * catálogo de produtos, ADR 0054). O valor é a soma dos itens com desconto
 * editável (10.4).
 */

interface ItemNaTela {
  chave: string
  descricao: string
  quantidade: string
  precoUnitario: string
}

function novoItem(): ItemNaTela {
  return {
    chave: `i${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    descricao: '',
    quantidade: '1',
    precoUnitario: '',
  }
}

export function ModalDesfecho({
  negociacao,
  etapa,
  motivos,
  aoFechar,
}: {
  negociacao: NegociacaoDto | null
  etapa: EtapaDto | null
  motivos: MotivoPerdaDto[]
  aoFechar: () => void
}) {
  const mover = useMoverEtapa()

  const [itens, setItens] = useState<ItemNaTela[]>([])
  const [desconto, setDesconto] = useState('')
  const [valorManual, setValorManual] = useState('')
  const [motivoId, setMotivoId] = useState('')
  const [observacao, setObservacao] = useState('')
  const [carregadoDe, setCarregadoDe] = useState<string | null>(null)

  const aberto = Boolean(negociacao && etapa)
  const chave = negociacao && etapa ? `${negociacao.id}:${etapa.id}` : null

  // Fechou: esquece o que carregou. Sem isto, abrir "novo" duas vezes seguidas
  // (ou reabrir a mesma ficha depois de Cancelar) trazia o que estava na tela antes.
  if (!aberto && carregadoDe !== null) setCarregadoDe(null)

  if (aberto && carregadoDe !== chave) {
    setCarregadoDe(chave)
    setItens([])
    setDesconto('')
    setValorManual(negociacao?.valor != null ? String(negociacao.valor).replace('.', ',') : '')
    setMotivoId('')
    setObservacao('')
  }

  const ehGanho = etapa?.tipo === 'ganho'
  const erro = mover.error instanceof ErroRequisicao ? mover.error : null
  const ativos = motivos.filter((m) => m.ativo)

  const itensValidos = itens
    .map((i) => ({
      descricao: i.descricao.trim(),
      quantidade: lerMoeda(i.quantidade) ?? 0,
      precoUnitario: lerMoeda(i.precoUnitario) ?? 0,
    }))
    .filter((i) => i.quantidade > 0 && i.descricao)

  const bruto = somarItens(itensValidos)
  const descontoNumero = lerMoeda(desconto) ?? 0
  const totalComItens = Math.max(0, Number((bruto - descontoNumero).toFixed(2)))
  const totalManual = lerMoeda(valorManual)
  const usandoItens = itens.length > 0

  const podeConfirmar = ehGanho
    ? usandoItens
      ? itensValidos.length > 0
      : totalManual !== null
    : Boolean(motivoId)

  function alterarItem(chaveItem: string, mudanca: Partial<ItemNaTela>) {
    setItens((atuais) => atuais.map((i) => (i.chave === chaveItem ? { ...i, ...mudanca } : i)))
  }

  function confirmar() {
    if (!negociacao || !etapa) return

    if (ehGanho) {
      mover.mutate(
        {
          id: negociacao.id,
          dados: {
            etapaId: etapa.id,
            ganho: usandoItens
              ? { itens: itensValidos, desconto: descontoNumero }
              : { itens: [], desconto: 0, valorTotal: totalManual ?? 0 },
          },
        },
        { onSuccess: aoFechar },
      )
    } else {
      if (!motivoId) return
      mover.mutate(
        {
          id: negociacao.id,
          dados: {
            etapaId: etapa.id,
            perda: { motivoId, observacao: observacao || undefined },
          },
        },
        { onSuccess: aoFechar },
      )
    }
  }

  return (
    <Modal
      aberto={aberto}
      titulo={
        // O título do ciclo entra quando existe: com dois abertos do mesmo
        // cliente, só o nome não diz qual deles está sendo fechado.
        ehGanho
          ? `Registrar ganho: ${negociacao?.contatoNome ?? ''}${negociacao?.titulo ? ` · ${negociacao.titulo}` : ''}`
          : `Registrar perda: ${negociacao?.contatoNome ?? ''}${negociacao?.titulo ? ` · ${negociacao.titulo}` : ''}`
      }
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={mover.isPending}
            disabled={!podeConfirmar}
            onClick={confirmar}
          >
            {ehGanho ? 'Registrar ganho' : 'Registrar perda'}
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

        {ehGanho ? (
          <>
            {itens.length === 0 ? (
              <>
                <Campo
                  rotulo="Valor da venda"
                  value={valorManual}
                  onChange={(e) => setValorManual(e.target.value)}
                  placeholder="1500,00"
                  erro={erro?.campo('ganho.valorTotal')}
                  autoFocus
                />
                <p className="text-xs text-tinta-400">
                  Ou detalhe os itens abaixo, e aí o valor vira a soma deles.
                </p>
              </>
            ) : null}

            <div className="space-y-2">
              {itens.map((item, indice) => (
                <div
                  key={item.chave}
                  className="grid grid-cols-[1fr_70px_100px_28px] items-end gap-2 rounded-md ring-1 ring-tinta-200 p-2"
                >
                  <div>
                    <span className="mb-1 block text-xs font-medium text-tinta-600">
                      Item {indice + 1}
                    </span>
                    <input
                      value={item.descricao}
                      onChange={(e) => alterarItem(item.chave, { descricao: e.target.value })}
                      placeholder="O que foi vendido"
                      className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-2 py-1.5 text-sm"
                    />
                  </div>

                  <label className="block">
                    <span className="mb-1 block text-xs text-tinta-400">Qtd</span>
                    <input
                      value={item.quantidade}
                      onChange={(e) => alterarItem(item.chave, { quantidade: e.target.value })}
                      className="w-full rounded-md ring-1 ring-tinta-200 px-2 py-1.5 text-sm"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs text-tinta-400">Preço</span>
                    <input
                      value={item.precoUnitario}
                      onChange={(e) => alterarItem(item.chave, { precoUnitario: e.target.value })}
                      placeholder="0,00"
                      className="w-full rounded-md ring-1 ring-tinta-200 px-2 py-1.5 text-sm"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => setItens((a) => a.filter((i) => i.chave !== item.chave))}
                    className="mb-1.5 rounded px-1 text-tinta-400 hover:text-red-600"
                    aria-label="Remover item"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <Botao className="w-full" onClick={() => setItens((a) => [...a, novoItem()])}>
              {itens.length === 0 ? 'Detalhar itens' : 'Adicionar item'}
            </Botao>

            {usandoItens ? (
              <div className="rounded-md ring-1 ring-tinta-200 bg-tinta-50 p-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-tinta-600">Soma dos itens</span>
                  <span className="tabular-nums">{formatarMoeda(bruto)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-tinta-600">Desconto</span>
                  <input
                    value={desconto}
                    onChange={(e) => setDesconto(e.target.value)}
                    placeholder="0,00"
                    className="w-28 rounded-md ring-1 ring-tinta-200 px-2 py-1 text-right text-sm"
                  />
                </div>
                <div className="mt-1 flex justify-between border-t border-tinta-200 pt-1 font-medium">
                  <span>Total</span>
                  <span className="tabular-nums">{formatarMoeda(totalComItens)}</span>
                </div>
              </div>
            ) : null}

            <Aviso>
              Isto grava uma venda com a data de hoje. O card fica na etapa de ganho; para começar
              de novo com o mesmo cliente, use &quot;nova negociação&quot; no card.
            </Aviso>
          </>
        ) : (
          <>
            {ativos.length === 0 ? (
              <Aviso tipo="atencao">
                Não há motivo de perda cadastrado. Crie ao menos um em Configuração → Etiquetas e
                motivos.
              </Aviso>
            ) : (
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-tinta-800">
                  Motivo da perda
                </span>
                <select
                  value={motivoId}
                  onChange={(e) => setMotivoId(e.target.value)}
                  className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
                >
                  <option value="">Escolha…</option>
                  {ativos.map((motivo) => (
                    <option key={motivo.id} value={motivo.id}>
                      {motivo.nome}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-tinta-800">
                Observação (opcional)
              </span>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                rows={3}
                className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
                placeholder="Fechou com o concorrente por causa do prazo."
              />
            </label>
          </>
        )}
      </div>
    </Modal>
  )
}
